// The app's cookie panel (Edward, Oct 26), with the same choice as the site's (gp_consent on the
// shared cookie domain), so choosing on either counts for both and no one is asked twice. By where
// they are (the API decides, GET /v1/consent; apps/api/src/lib/consent.ts):
//
// - ask_first (the EU, EEA, UK, Switzerland, or unknown): it opens on the app's way in (the start
//   page, event, personal and rater links) until they choose. Reject and Accept look the same.
// - opt_out (everywhere else): no banner; ad tracking is on until they opt out with "Your privacy
//   choices" in the footer, or on /privacy-choices.
// - gpc (the browser's privacy signal is on): it only says tracking is off.
//
// Nothing changes on the page: our server reads the choice when it sends a step. While it's open at
// the bottom, the page gets room, so the Start button is never stuck under it.

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { get, request } from "../lib/api";
import { WEBSITE_PRIVACY_URL } from "../lib/copy";
import { CONSENT_ACCEPTED_EVENT } from "../lib/page-views";

/** The app's way in: where it may open by itself. */
const ENTRY = /^\/(?:$|go\/|i$|r\/)/;
const OPEN_EVENT = "gp:privacy-choices";

export type ConsentMode = "ask_first" | "opt_out" | "gpc";
export interface ConsentState {
  ask: boolean;
  choice: "accepted" | "rejected" | null;
  mode: ConsentMode;
  /** Whether ad tracking is on for them right now. */
  allowed: boolean;
}

/** Opens the panel (the footer's "Your privacy choices"). */
export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Where they stand, from the API once `active` (null until it answers, or if it can't). */
export function useConsent(active = true): [ConsentState | null, (choice: "accepted" | "rejected") => Promise<void>] {
  const [state, setState] = useState<ConsentState | null>(null);
  useEffect(() => {
    if (!active || state) return;
    let live = true;
    get<ConsentState>("/v1/consent").then(
      (r) => live && setState(r),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [active, state]);
  const choose = async (choice: "accepted" | "rejected") => {
    await request("POST", "/v1/consent", { body: { choice } }).catch(() => undefined);
    setState((s) => (s ? { ...s, ask: false, choice, allowed: choice === "accepted" && s.mode !== "gpc" } : s));
    // The page they're on wasn't sent to Meta before they chose (lib/page-views.ts).
    if (choice === "accepted") window.dispatchEvent(new Event(CONSENT_ACCEPTED_EVENT));
  };
  return [state, choose];
}

const TEXT: Record<ConsentMode, { title: string; text: string }> = {
  ask_first: {
    title: "Cookies and ads",
    text: `We'd like to use cookies and similar tools from advertising and analytics partners (like Meta and Google) to measure our ads and show you relevant ones. They stay off unless you accept. You can change your mind any time with "Your privacy choices" at the bottom of the page.`,
  },
  opt_out: {
    title: "Your privacy choices",
    text: "We use cookies and similar tools from partners like Meta and Google to measure our ads and show you relevant ones. You can turn this off.",
  },
  gpc: {
    title: "Your privacy choices",
    text: "Your browser's privacy signal (Global Privacy Control) is on, so ad tracking is off. Nothing from this site goes to advertising partners.",
  },
};

/** The panel's words and buttons. `onDone` after a choice (or Close). */
export function ConsentPanel({
  state,
  choose,
  onDone,
  showStatus = false,
}: {
  state: ConsentState;
  choose: (c: "accepted" | "rejected") => Promise<void>;
  onDone?: () => void;
  showStatus?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const t = TEXT[state.mode];
  const pick = async (c: "accepted" | "rejected") => {
    setBusy(true);
    await choose(c);
    setBusy(false);
    onDone?.();
  };
  return (
    <div className={`w-full max-w-[560px] rounded-[16px] border border-line bg-panel p-5 ${onDone ? "shadow-[0_8px_32px_rgba(0,0,0,0.18)]" : ""}`}>
      <p className="mb-1.5 text-[16px] font-bold">{t.title}</p>
      <p className="text-[15px] leading-relaxed">
        {t.text}{" "}
        <a href={WEBSITE_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
      </p>
      {showStatus && state.mode !== "gpc" ? (
        <p className="mt-3 text-[14px] font-semibold" role="status" data-testid="consent-status">
          Ad tracking is {state.allowed ? "on" : "off"} for you.
        </p>
      ) : null}
      {state.mode === "gpc" && !onDone ? null : (
        <div className="mt-4 flex gap-2.5">
          {state.mode === "gpc" ? (
            <button type="button" className="btn btn-secondary min-h-12 flex-1" onClick={onDone}>
              Close
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-primary min-h-12 flex-1" disabled={busy} onClick={() => void pick("rejected")}>
                {state.mode === "opt_out" ? "Opt out of ad tracking" : "Reject"}
              </button>
              <button type="button" className="btn btn-primary min-h-12 flex-1" disabled={busy} onClick={() => void pick("accepted")}>
                {state.mode === "opt_out" ? "Keep it on" : "Accept"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The floating panel: opens by itself on the way in where they're asked first, and from the footer link. */
export function ConsentBanner() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const entry = ENTRY.test(pathname);
  // Only asked on the way in, or once someone opens it: other pages (the test, the PDF) ask nothing.
  const [state, choose] = useConsent(entry || open);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!entry) setOpen(false);
    else if (state?.ask) setOpen(true);
  }, [state?.ask, entry]);

  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  // Room at the bottom of the page while it's open, so nothing is stuck under it.
  useEffect(() => {
    const el = box.current;
    if (!open || !el) return;
    const fit = () => (document.body.style.paddingBottom = `${el.offsetHeight + 16}px`);
    fit();
    // Older browsers (and tests) without ResizeObserver: the first fit stands.
    const watch = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(fit);
    watch?.observe(el);
    return () => {
      watch?.disconnect();
      document.body.style.paddingBottom = "";
    };
  }, [open, state]);

  if (!open || !state || pathname === "/privacy-choices") return null;
  return (
    <div ref={box} role="region" aria-label="Your privacy choices" className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-[max(16px,env(safe-area-inset-bottom))]">
      <ConsentPanel state={state} choose={choose} onDone={() => setOpen(false)} />
    </div>
  );
}
